/**
 * DrawingPreview - Real-time drawing preview lines and control point overlays
 * Extracted from Application.js (pure code motion; delegation pattern).
 *
 * The preview object references (currentDrawingPreview /
 * currentDrawingPreviewPlane) stay on the Application - they are read from
 * the pointer handlers there.
 */
import * as THREE from 'three';
import { SketchSpline } from '../sketch/SketchSpline.js';

export class DrawingPreview {
    constructor(app) {
        this.app = app;
    }

    /**
     * Create real-time drawing preview line (Phase 3.5)
     * @param {number} planeIndex - Index of the plane being drawn on
     */
    createDrawingPreview(planeIndex) {
        if (!this.app.USE_UNIFIED_VIEW) return;

        // Remove any existing preview
        this.removeDrawingPreview();

        // Store plane index for updates
        this.app.currentDrawingPreviewPlane = planeIndex;
    }

    /**
     * Update real-time drawing preview line (Phase 3.5)
     * @param {number} planeIndex - Index of the plane being drawn on
     */
    updateDrawingPreview(planeIndex) {
        if (!this.app.USE_UNIFIED_VIEW) return;

        // Support both currentPath (pen tool) and currentSpline (brush tool)
        const currentPath = this.app.sketchPlane.currentPath;
        const currentSpline = this.app.sketchPlane.currentSpline;

        if (!currentPath && !currentSpline) {
            return;
        }

        const basePlane = this.app.basePlanes[planeIndex];

        // Get points from the correct property
        let points = null;
        if (currentPath) {
            points = currentPath.points;
        } else if (currentSpline) {
            // Splines store points in centrePath
            points = currentSpline.centrePath?.points;
        }

        if (!points || points.length < 2) {
            return;
        }

        // Remove old preview
        this.removeDrawingPreview(false);

        // SketchSpline.build() runs on every pointermove, so the built offset
        // outline is available live during the drag.
        const outlinePoints = currentSpline?.path?.points;

        this.app.currentDrawingPreview = this.createPreviewGroup(
            points,
            outlinePoints,
            basePlane.position.z + 0.02 // Slightly above plane
        );

        // Add to the shared overlay frame so the preview joins the rotation
        // pivot AND follows the chair's physics pose, lying ON the (possibly
        // rotated/simulated) plane being drawn on
        this.app.addOverlay(this.app.currentDrawingPreview);
    }

    /**
     * Build a preview group: red centerline + dark offset-outline loop
     * @param {Array} centerPoints - Centre path points (sketch pixels)
     * @param {Array|null} outlinePoints - Built offset outline points, if any
     * @param {number} zOffset - World Z to draw at
     * @returns {THREE.Group}
     */
    createPreviewGroup(centerPoints, outlinePoints, zOffset) {
        const previewGroup = new THREE.Group();
        const toWorld = (p) => new THREE.Vector3(p.x * 0.01, p.y * 0.01, zOffset);

        // Centerline (the path being drawn/edited)
        if (centerPoints && centerPoints.length >= 2) {
            const centerGeometry = new THREE.BufferGeometry().setFromPoints(centerPoints.map(toWorld));
            const centerMaterial = new THREE.LineBasicMaterial({
                color: 0xFF4444, // Red for preview
                linewidth: 1,
                transparent: true,
                opacity: 0.6,
                depthTest: false // Always visible on top
            });
            const centerLine = new THREE.Line(centerGeometry, centerMaterial);
            centerLine.renderOrder = 999;
            previewGroup.add(centerLine);
        }

        // Offset outline preview (the actual stroke shape)
        if (outlinePoints && outlinePoints.length >= 3) {
            const outlineGeometry = new THREE.BufferGeometry().setFromPoints(outlinePoints.map(toWorld));
            const outlineMaterial = new THREE.LineBasicMaterial({
                color: 0x222222, // Dark outline like the final part
                linewidth: 2,
                transparent: true,
                opacity: 0.9,
                depthTest: false
            });
            const outlineLoop = new THREE.LineLoop(outlineGeometry, outlineMaterial);
            outlineLoop.renderOrder = 999;
            previewGroup.add(outlineLoop);
        }

        return previewGroup;
    }

    /**
     * Lightweight preview while dragging a control point with the select tool.
     * Shows the rebuilt outline live without regenerating meshes/slices
     * (full regeneration happens on pointer-up via onShapeChange).
     * @param {Object} spline - The spline being edited
     */
    updateSelectedSplinePreview(shape) {
        if (!this.app.USE_UNIFIED_VIEW || !shape) return;

        // Draw on the first selected base plane
        const planeIndex = this.app.selectedBasePlaneIndices.values().next().value ?? 0;
        const basePlane = this.app.basePlanes[planeIndex];
        if (!basePlane) return;

        this.removeDrawingPreview(false);

        // Splines have a centre path + built outline; polygon paths ARE the
        // outline (sample beziers so curved edges preview as curves)
        let centerPoints = null;
        let outlinePoints = null;
        if (shape.centrePath) {
            centerPoints = shape.centrePath.points;
            outlinePoints = shape.path?.points;
        } else if (shape.points) {
            outlinePoints = this.sampleShapePoints(shape.points);
        }

        this.app.currentDrawingPreview = this.createPreviewGroup(
            centerPoints,
            outlinePoints,
            basePlane.position.z + 0.02
        );

        this.app.addOverlay(this.app.currentDrawingPreview);
    }

    /**
     * Sample a point list into a dense polyline, subdividing bezier
     * segments (cp2 of the segment start, cp1 of the segment end)
     * @param {Array} points - SketchPoints, possibly with control points
     * @returns {Array<{x,y}>}
     */
    sampleShapePoints(points) {
        const out = [];
        const n = points.length;
        for (let i = 0; i < n; i++) {
            const cur = points[i];
            const next = points[(i + 1) % n];
            out.push({ x: cur.x, y: cur.y });

            const cp1 = cur.controlPoint2;
            const cp2 = next.controlPoint1;
            if (cp1 || cp2) {
                const c1 = cp1 || cur;
                const c2 = cp2 || next;
                const STEPS = 10;
                for (let s = 1; s < STEPS; s++) {
                    const t = s / STEPS;
                    const t1 = 1 - t;
                    out.push({
                        x: t1 * t1 * t1 * cur.x + 3 * t1 * t1 * t * c1.x + 3 * t1 * t * t * c2.x + t * t * t * next.x,
                        y: t1 * t1 * t1 * cur.y + 3 * t1 * t1 * t * c1.y + 3 * t1 * t * t * c2.y + t * t * t * next.y
                    });
                }
            }
        }
        return out;
    }

    /**
     * Remove real-time drawing preview (Phase 3.5)
     * @param {boolean} [clearPlane=true] - Also forget the preview plane index
     */
    removeDrawingPreview(clearPlane = true) {
        if (this.app.currentDrawingPreview) {
            this.app.currentDrawingPreview.parent?.remove(this.app.currentDrawingPreview);
            this.app.currentDrawingPreview.traverse(obj => {
                if (obj.geometry) obj.geometry.dispose();
                if (obj.material) obj.material.dispose();
            });
            this.app.currentDrawingPreview = null;
        }
        if (clearPlane) {
            this.app.currentDrawingPreviewPlane = null;
        }
    }

    /**
     * Render control points in 3D for selected planes when select tool is active (Phase 3.5+)
     */
    renderControlPoints() {
        if (!this.app.USE_UNIFIED_VIEW || !this.app.controlPointRenderer) {
            return;
        }

        // Keep control points a constant size on screen regardless of zoom
        this.app.controlPointRenderer.setViewScale(1 / this.app.cameraController.zoom);

        // Control points live in the shared overlay frame (inside the
        // rotation pivot) so they sit on the rotated planes AND follow the
        // chair's physics pose (raycast hit positions match what is seen)
        const overlayContainer = this.app.overlayFrame
            || this.app.sceneRotation?.getPivotGroup()
            || this.app.sceneManager.scene;

        // Only show control points when a point-editing tool is active
        if (this.app.sketchPlane.tool !== 'select' && this.app.sketchPlane.tool !== 'bezier') {
            // Clear any existing control points if tool changed
            this.app.controlPointRenderer.clearAll(overlayContainer);
            return;
        }

        // Clear old control points
        this.app.controlPointRenderer.clearAll(overlayContainer);

        // Render control points for each selected plane.
        // IMPORTANT: render the LIVE sketch objects (sketchPlane.splines/paths),
        // not the clones in basePlaneSketchStates - the live objects are what
        // hit detection operates on and what moves during a drag. Built
        // outline shapes (closedShapes) get no control dots: their dense
        // vertices are not editable, the centre path is.
        // SELECT tool is Figma-like: overlays (thick centre line, anchors,
        // handles) render ONLY for shapes in the shape selection -
        // unselected shapes stay clean. The bezier tool keeps overlays on
        // everything (its whole job is grabbing any point's handles).
        const gateToSelection = this.app.sketchPlane.tool === 'select';
        const shapeSel = this.app.sketchPlane.selectedShapes;

        let totalRendered = 0;
        for (const planeIndex of this.app.selectedBasePlaneIndices) {
            const basePlane = this.app.basePlanes[planeIndex];
            if (!basePlane) continue;

            // Control points for splines (brush/leg/offset strokes).
            // LEG splines are scoped to planes that actually OWN legs: legs
            // live only on the outer layers (cloneSketchStateForPlane strips
            // them from inner planes), so drawing their overlay on every
            // selected plane advertised geometry the plane doesn't have.
            this.app.sketchPlane.splines?.forEach((spline, idx) => {
                const isLegSpline = spline.type === SketchSpline.TYPE_LEG;
                if (isLegSpline && !this.app.isOuterBasePlane(planeIndex)) return;
                if (gateToSelection && !shapeSel?.has(spline)) return;

                const splineId = `plane${planeIndex}_spline${idx}`;
                const isSelected = gateToSelection
                    ? true // everything rendered IS selected (bright cyan)
                    : this.app.sketchPlane.selectedSpline === spline;

                this.app.controlPointRenderer.renderSplineControls(
                    spline,
                    basePlane,
                    splineId,
                    overlayContainer,
                    isSelected
                );
                totalRendered++;
            });

            // Control points for pen-tool paths. Use the spline renderer -
            // it falls back to .points and also draws bezier handles, which
            // polygon points can carry after bezier-tool editing.
            this.app.sketchPlane.paths?.forEach((path, idx) => {
                if (gateToSelection && !shapeSel?.has(path)) return;

                const pathId = `plane${planeIndex}_path${idx}`;
                const isSelected = gateToSelection
                    ? true
                    : this.app.sketchPlane.selectedPath === path;

                this.app.controlPointRenderer.renderSplineControls(
                    path,
                    basePlane,
                    pathId,
                    overlayContainer,
                    isSelected
                );
                totalRendered++;
            });
        }

        // Log only once per frame when control points are rendered
        if (totalRendered > 0 && !this._controlPointsLogged) {
            console.log(`[CONTROL_POINTS] Rendered ${totalRendered} control point groups for ${this.app.selectedBasePlaneIndices.size} selected planes`);
            this._controlPointsLogged = true;
            // Reset flag after 1 second to allow periodic logging
            setTimeout(() => this._controlPointsLogged = false, 1000);
        }
    }
}
